import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook'
import { saveAsNewTemplateThunk } from '@/common/reducers/CampaignThunks'
import { Button, IconButton, Tooltip } from '@mui/material'
import { Box, Stack } from '@mui/system'
import { BookmarkPlus } from 'lucide-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { namespace } from '@common/config/constants'
import { MessageEditorAddAttachmentButton } from './message-editor-add-attachment-button'
import { MessageEditorFontFormatButtons, useEditorToolbarTooltipSlotProps } from './message-editor-font-format-buttons'
import MessageEditorTemplateDropdown from './message-editor-template-dropdown'
import SaveNewTemplateModal from './save-new-template-modal'

type Props = {
    fileInputRef: React.RefObject<HTMLInputElement>;
    emojiButtonRef: React.RefObject<HTMLButtonElement>;
    onFormatRequest?: (symbol: '*' | '~' | '_' | '```' | '`' | 'bullet' | 'number' | 'quote') => void;
    onEmojiPickerToggle?: () => void;
}

export default function MessageEditorTemplateHeaderAndTopButtons({
    fileInputRef,
    emojiButtonRef,
    onFormatRequest,
    onEmojiPickerToggle,
}: Props) {
    const { t } = useTranslation(namespace);
    const tooltipSlotProps = useEditorToolbarTooltipSlotProps();
    const dispatch = useAppDispatch();
    const activeBulkView = useAppSelector((state) => state.CampaignReducer.activeBulkView);
    const templateId = useAppSelector((state) => state.ScheduledMessageEditorReducer.templateId);
    const [isSaveNewTemplateModalOpen, setIsSaveNewTemplateModalOpen] = useState(false);
    
    // Read state directly from Redux
    const message = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.message);
    const editorContext = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.editorContext);
    const isPoll = useAppSelector((state: any) => !!state.ScheduledMessageEditorReducer.isPoll);
    const poll = useAppSelector((state: any) => state.ScheduledMessageEditorReducer.poll);

    const isEditMode = activeBulkView === "campaignNewTemplateViewFromManagement"
        || activeBulkView === "campaignNewTemplateViewFromNewCampaignView"
        || activeBulkView === "campaignNewTemplateViewFromWhatsappView";

    const isMessageEmpty = !message || !message.trim();
    // Poll-as-template support: a poll is saveable when its question is
    // non-empty AND it has at least 2 options with non-empty text. We need
    // to consider polls here too — otherwise the "Save as new template"
    // button never appears for a poll because `isMessageEmpty` is always
    // true in poll mode (the text body is unused for polls).
    const validOptionCount = Array.isArray(poll?.options)
        ? poll.options.filter((o: any) => typeof o?.text === 'string' && o.text.trim().length > 0).length
        : 0;
    const isPollValid = isPoll
        && typeof poll?.question === 'string'
        && poll.question.trim().length > 0
        && validOptionCount >= 2;
    const hasSaveableContent = isPoll ? isPollValid : !isMessageEmpty;

    const saveNewTemplateButton = () => {
        return (
            <>
                {hasSaveableContent && templateId &&
                    <Button 
                        onClick={() => setIsSaveNewTemplateModalOpen(true)} 
                        sx={{
                            textTransform: 'none',
                            height: '36px',
                            fontWeight: 600,
                            fontSize: '14px',
                            lineHeight: '14px',
                            color: 'var(--color-primary)',
                            border: '0px',
                            padding: '4px 12px',
                            backgroundColor: 'var(--color-surface)',
                            borderRadius: '10px',
                            marginRight: '5px',
                            '&:hover': {
                                backgroundColor: 'var(--color-surface)',
                                filter: 'brightness(0.95)',
                            },
                        }}
                    >
                        Save as new template
                    </Button>
                }
            </>
        )
    }


    return (
        <>
            <Stack direction='row' className="bt-msg-editor-toolbar" sx={{
                alignItems: 'center',
                borderBottom: '1px solid var(--tasks-cell-border)',
                //backgroundColor: '#fafbfc',
                mt: '0px !important',
                borderRadius: '10px 10px 0px 0px',
                // FE#384 — WRAP only when there is no room, and start the wrapped row from
                // the LEFT. The format group `flex: 1 1 auto` grows to fill a single row so
                // Add files/Templates sit on the right when they fit; when the row is too
                // narrow the trailing group drops to a second line at flex-start (no
                // auto-margin, no `space-between` — both of which parked the wrapped item on
                // the right). rowGap keeps the two lines from touching.
                flexWrap: 'wrap',
                minWidth: 0,
                columnGap: '8px',
                rowGap: '4px',
                py: '2px',
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 0,
                    // Grow to fill a single row (pushing the trailing group to the right);
                    // when the trailing group wraps below, take the whole first line so the
                    // icons lay out horizontally instead of cramming into a column.
                    flex: '1 1 auto',
                }}>
                    <MessageEditorFontFormatButtons
                        emojiButtonRef={emojiButtonRef}
                        onFormatRequest={onFormatRequest}
                        onEmojiPickerToggle={onEmojiPickerToggle}
                    />
                </Box>

                <Stack direction='row' sx={{
                    alignItems: 'center',
                    flexShrink: 0,
                    gap: 0.5,
                    // No auto-margin: the format group's flex-grow already right-aligns this
                    // on a single row, and on a wrapped second line this then starts at the
                    // left (flex-start).
                    paddingInlineEnd: '7px',
                }}>
                    <MessageEditorAddAttachmentButton inputRef={fileInputRef} />
                    {isEditMode && saveNewTemplateButton()}
                    {!isEditMode && (
                        // Icon-only beside Templates, so it costs the toolbar one
                        // icon's width; the tooltip names it. Same place in every
                        // editor that has Templates (scheduled message + campaigns).
                        // The span keeps the tooltip alive while the button is
                        // disabled (nothing saveable yet).
                        <Tooltip title={t('messageEditor.saveAsTemplate', 'Save as template')} arrow placement="top" slotProps={tooltipSlotProps}>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => setIsSaveNewTemplateModalOpen(true)}
                                    disabled={!hasSaveableContent}
                                    aria-label={t('messageEditor.saveAsTemplate', 'Save as template')}
                                    sx={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '6px',
                                        color: 'var(--color-primary)',
                                        '&.Mui-disabled': { color: 'var(--color-text)', opacity: 0.35 },
                                    }}
                                >
                                    <BookmarkPlus size={18} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                    {!isEditMode && <MessageEditorTemplateDropdown />}
                </Stack>
            </Stack>

            <SaveNewTemplateModal
                isSaveNewTemplateModalOpen={isSaveNewTemplateModalOpen}
                onClose={() => setIsSaveNewTemplateModalOpen(false)}
            />
        </>
    )
}
